import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import DataTable from 'components/shared/DataTable';
import Checkbox from 'components/shared/form/Checkbox';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import { PERMISSIONS } from 'models/Profile';
import {
  adminPortalSystemQueryKeys,
  AdminRetrievalField,
  AdminScanField,
  createAdminPortalSystem,
  showAdminPortalSystem,
  updateAdminPortalSystem,
} from 'services/api/admin/portalSystems';
import { portalSystemQueryKeys } from 'services/api/portalSystems';
import { colors } from 'styles/theme/colors';

import { primaryIdLabel } from './PortalSystems';

// Admin → Portal Systems → Add/Edit. Adding a portal is an INSERT (OLIO-3195): create
// carries just the names and the home URL — a new row's field config is the defaults, so
// the collected-field tables stay an edit concern. Editing (OLIO-3074) is the selection
// side of the portal's collected-field configuration: the universe of collectable fields
// is code, so the tables list every field and lock the non-negotiables; the form only
// carries what the admin can move.
type FormValues = {
  systemName: string;
  displayName: string;
  retrievalFields: string[];
  scanFields: string[];
  sweepTableFields: string[];
  primaryId: string;
  urlRegex: string;
  homeUrl: string;
  urlIdentifiesReferral: boolean;
  worklistMarkers: boolean;
  identifierRegex: string;
};

type PrimaryIdOption = { value: string; label: string };

function compiles(pattern: string) {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

// Capture group 1 must open the pattern. A named group `(?<name>` IS capture group 1
// in both engines, so it passes; every other `(?…` opener (non-capturing `(?:`,
// lookarounds `(?=` `(?!` `(?<=` `(?<!`, inline flags) is not a capture and fails.
function startsWithCaptureGroup(pattern: string) {
  if (!pattern.startsWith('(')) return false;
  return !pattern.startsWith('(?') || /^\(\?<[A-Za-z_][A-Za-z0-9_]*>/.test(pattern);
}

// Whether group 1 exists anywhere, mirroring the api's `url_regex_has_capture_group?` — matched
// against the empty string with everything made optional, so a pattern that cannot match a real
// URL still answers. Deliberately not startsWithCaptureGroup, which is the URL-mode positional
// rule and would reject patterns the api accepts.
function hasCaptureGroup(pattern: string) {
  if (!pattern) return false;
  try {
    return (new RegExp(`${pattern}|`).exec('')?.length ?? 0) > 1;
  } catch {
    return false;
  }
}

// A pattern that matches the empty string matches every text node on the page, which is the
// mistake this form exists to catch before the api does: `\d*` reads like an identifier pattern.
function matchesEverything(pattern: string) {
  try {
    return new RegExp(pattern).test('');
  } catch {
    return false;
  }
}

// trim() on the names so a whitespace-only one fails required() instead of passing
// validation and 422ing server-side with only the generic toast. The recognition
// pattern is required on every system — it is how the extension knows a page belongs
// to this portal — and it must compile. Only when the primary id is `url` must it
// START with a capture group, because then capture group 1 is the referral pointer.
const schema = yup.object({
  systemName: yup.string().trim().required('System name is required'),
  displayName: yup.string().trim().required('Display name is required'),
  homeUrl: yup
    .string()
    .trim()
    .required('Home URL is required')
    .matches(/^https:\/\/[^/\s]+/i, 'Must be an https URL'),
  urlRegex: yup
    .string()
    .trim()
    .required('Referral URL pattern is required — it is how the extension recognizes this portal')
    .test('compiles', 'Must be a valid regular expression', (value) => !value || compiles(value))
    .test('capture-group', 'Must start with a capture group — the capture is the referral pointer', function (value) {
      return this.parent.primaryId !== 'url' || !value || startsWithCaptureGroup(value);
    }),
  // The claim aliases capture group 1, so it needs one. Guarded here because the api refuses the
  // save and the form has no 422-to-field plumbing — without this the only feedback is the
  // generic error toast, with nothing pointing at the pattern.
  urlIdentifiesReferral: yup
    .boolean()
    .test(
      'needs-capture',
      'Needs a capture group in the pattern above — that capture is what identifies the referral',
      function (value) {
        return !value || this.parent.primaryId === 'url' || hasCaptureGroup(this.parent.urlRegex ?? '');
      }
    ),
  identifierRegex: yup
    .string()
    .trim()
    .test('compiles', 'Must be a valid regular expression', (value) => !value || compiles(value))
    .test(
      'matches-everything',
      'Matches the empty string, so it would mark every piece of text on the page',
      (value) => !value || !matchesEverything(value)
    ),
  // URL mode marks links with the referral pattern it already has. Field mode has nothing to test
  // text against until a pattern exists, so the opt-in there would switch nothing on.
  worklistMarkers: yup
    .boolean()
    .test(
      'needs-identifier-pattern',
      'Needs an identifier pattern below — on this portal a referral is identified by a field, not the URL',
      function (value) {
        return !value || this.parent.primaryId === 'url' || !!(this.parent.identifierRegex ?? '').trim();
      }
    ),
});

// A claimed URL needs a capture that varies per referral, so the host-only field-mode suggestion
// would be a pattern the api refuses: it captures nothing on the portal's own pages. Anchoring at
// the host with the capture optional satisfies the claim and still recognizes the portal's list
// pages, where no referral appears.
function placeholderPattern(primaryId: string, claimsUrl: boolean) {
  if (primaryId === 'url') return String.raw`(portal\.example\.com/referrals/\d+)`;

  return claimsUrl ? String.raw`portal\.example\.com(?:/referrals/([^/?#]+))?` : String.raw`^portal\.example\.com(/|$)`;
}

function toggled(names: string[], name: string, on: boolean) {
  return on ? [...names, name] : names.filter((n) => n !== name);
}

export default function EditPortalSystem() {
  const { id } = useParams();
  const isEditing = !!id;
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);

  const form = useAdminForm({
    id,
    show: showAdminPortalSystem,
    create: async (payload: FormValues) => {
      const created = await createAdminPortalSystem({
        systemName: payload.systemName,
        displayName: payload.displayName,
        homeUrl: payload.homeUrl,
        urlRegex: payload.urlRegex.trim(),
      });
      queryClient.invalidateQueries({ queryKey: portalSystemQueryKeys.base });
      return created;
    },
    update: async (updateId: string, payload: FormValues) => {
      // systemName is the identifier on the wire — create-only, never sent on update.
      const { systemName: _createOnly, ...rest } = payload;
      const updated = await updateAdminPortalSystem(updateId, rest);
      queryClient.invalidateQueries({ queryKey: portalSystemQueryKeys.base });
      return updated;
    },
    include: [] as const,
    queryKey: adminPortalSystemQueryKeys,
    schema,
    toFormValues: (data): FormValues => ({
      systemName: data?.systemName ?? '',
      displayName: data?.displayName ?? '',
      retrievalFields: (data?.retrievalFields ?? []).filter((f) => f.enabled && !f.required).map((f) => f.name),
      scanFields: (data?.scanFields ?? []).filter((f) => f.enabled && !f.required).map((f) => f.name),
      sweepTableFields: (data?.scanFields ?? []).filter((f) => f.showOnSweepsTable).map((f) => f.name),
      primaryId: data?.primaryId ?? 'url',
      urlRegex: data?.urlRegex ?? '',
      homeUrl: data?.homeUrl ?? '',
      urlIdentifiesReferral: data?.urlIdentifiesReferral ?? false,
      worklistMarkers: data?.worklistMarkers ?? false,
      identifierRegex: data?.identifierRegex ?? '',
    }),
    toPayload: (values) => ({
      ...values,
      systemName: values.systemName.trim(),
      displayName: values.displayName.trim(),
      urlRegex: values.urlRegex.trim() || null,
      urlIdentifiesReferral: values.primaryId !== 'url' && values.urlIdentifiesReferral,
      identifierRegex: values.identifierRegex.trim() || null,
    }),
    onCreateSuccess: { toast: 'Portal system successfully added!', navigate: '/admin/portal-systems' },
    onUpdateSuccess: { toast: 'Portal system successfully updated!', navigate: '/admin/portal-systems' },
    // The api holds rules this form cannot mirror without reimplementing Pointer canonicalization
    // against the portal's own home URL, so its message is the only accurate account of what is
    // wrong. useAdminForm surfaces it; these are the names this form gives the fields it can
    // refuse, since the api's own (`primaryId`, `urlRegex`) are not what the labels above say.
    onError: {
      toast: 'Something went wrong. Please try again.',
      fieldLabels: {
        primaryId: 'Primary ID',
        urlIdentifiesReferral: 'Referral identified by URL',
        urlRegex: 'Referral URL pattern',
        worklistMarkers: 'List page markers',
        identifierRegex: 'Identifier pattern',
        homeUrl: 'Home URL',
        retrievalFields: 'Retrieval fields',
        scanFields: 'Scan fields',
        sweepTableFields: 'Sweep table columns',
      },
    },
  });

  const system = form.data;
  const values = form.values;

  const isCollected = (field: AdminRetrievalField | AdminScanField, selected: string[]) =>
    field.required || selected.includes(field.name);

  // A scan field the admin turns off cannot stay a table column; the api rejects the
  // combination, so drop the column in the same change.
  const setScanField = (name: string, on: boolean) =>
    form.setValues({
      scanFields: toggled(values.scanFields, name, on),
      sweepTableFields: on ? values.sweepTableFields : values.sweepTableFields.filter((n) => n !== name),
    });

  const retrievalHelper = createColumnHelper<AdminRetrievalField>();
  const retrievalColumns = useMemo(
    () => [
      retrievalHelper.accessor('label', {
        header: 'Field',
        enableSorting: false,
        cell: (data) => <FieldName field={data.row.original} />,
      }),
      retrievalHelper.accessor('type', {
        header: 'Type',
        enableSorting: false,
        cell: (data) => <TypeTag>{data.getValue()}</TypeTag>,
      }),
      retrievalHelper.display({
        id: 'collected',
        header: 'Collected',
        size: 100,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={isCollected(field, values.retrievalFields)}
              disabled={!canEdit || field.required}
              onChange={(on) => form.setValue('retrievalFields', toggled(values.retrievalFields, field.name, on))}
              dataCyLabel={`retrieval-field-${field.name}`}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.retrievalFields, canEdit]
  );

  const scanHelper = createColumnHelper<AdminScanField>();
  const scanColumns = useMemo(
    () => [
      scanHelper.accessor('label', {
        header: 'Field',
        enableSorting: false,
        cell: (data) => <FieldName field={data.row.original} />,
      }),
      scanHelper.accessor('type', {
        header: 'Type',
        enableSorting: false,
        cell: (data) => <TypeTag>{data.getValue()}</TypeTag>,
      }),
      scanHelper.display({
        id: 'collected',
        header: 'Collected',
        size: 100,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={isCollected(field, values.scanFields)}
              disabled={!canEdit || field.required}
              onChange={(on) => setScanField(field.name, on)}
              dataCyLabel={`scan-field-${field.name}`}
            />
          );
        },
      }),
      scanHelper.display({
        id: 'sweepTable',
        header: 'Sweeps Table Column',
        size: 160,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={values.sweepTableFields.includes(field.name)}
              disabled={!canEdit || !isCollected(field, values.scanFields)}
              onChange={(on) => form.setValue('sweepTableFields', toggled(values.sweepTableFields, field.name, on))}
              dataCyLabel={`sweep-table-${field.name}`}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.scanFields, values.sweepTableFields, canEdit]
  );

  const primaryIdOptions: PrimaryIdOption[] = useMemo(
    () => (system ? system.primaryIdOptions.map((value) => ({ value, label: primaryIdLabel(system, value) })) : []),
    [system]
  );

  if (form.loading || (isEditing && !system)) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title={`${isEditing ? 'Edit' : 'Add'} Portal System`} subtitle={system?.displayName} />

      <SectionHeader>Portal</SectionHeader>

      {/* The wire identifier on scripts, runs, settings, and mappings — set once at
          creation, renames are a data migration. Display name is what UIs render and
          is freely editable. */}
      <InputGroup title='System Name (cannot be changed later)' error={form.errors.systemName}>
        <NameInput
          value={values.systemName}
          onChange={(e) => form.setValue('systemName', e.target.value)}
          placeholder='e.g. careport'
          disabled={isEditing || !canEdit}
          $hasError={!!form.errors.systemName}
          data-testid='portal-system-system-name'
        />
      </InputGroup>

      <InputGroup title='Display Name' error={form.errors.displayName}>
        <NameInput
          value={values.displayName}
          onChange={(e) => form.setValue('displayName', e.target.value)}
          placeholder='e.g. CarePort'
          disabled={!canEdit}
          $hasError={!!form.errors.displayName}
          data-testid='portal-system-display-name'
        />
      </InputGroup>

      {isEditing && system && (
        <>
          <HelpText>Collected fields apply to every client using this portal.</HelpText>

          <SectionHeader>Collected on Retrieval</SectionHeader>

          <TableWrapper data-testid='retrieval-fields-table'>
            <DataTable columns={retrievalColumns} data={system.retrievalFields} />
          </TableWrapper>

          <SectionHeader>Collected on Scans</SectionHeader>

          <TableWrapper data-testid='scan-fields-table'>
            <DataTable columns={scanColumns} data={system.scanFields} />
          </TableWrapper>
        </>
      )}

      <SectionHeader>Portal Home</SectionHeader>

      {/* The portal's own front door: where a first sweep recording starts, and the login
          anchor a credential save is verified against when nothing is recorded yet. */}
      <InputGroup title='Home URL (https only)' error={form.errors.homeUrl}>
        <UrlInput
          value={values.homeUrl}
          onChange={(e) => form.setValue('homeUrl', e.target.value.trim())}
          placeholder='https://portal.example.com/login'
          disabled={!canEdit}
          $hasError={!!form.errors.homeUrl}
          data-testid='portal-system-home-url'
        />
      </InputGroup>

      <InputGroup title='Referral URL pattern' error={getFieldError(form.errors, 'urlRegex')}>
        <Input
          value={values.urlRegex}
          onChange={(e) => form.setValue('urlRegex', e.target.value)}
          placeholder={placeholderPattern(values.primaryId, values.urlIdentifiesReferral)}
          $hasError={Boolean(getFieldError(form.errors, 'urlRegex'))}
          disabled={!canEdit}
          data-testid='url-regex'
        />
        <RegexHelpText>
          {values.primaryId === 'url'
            ? 'Tested against the URL body — no scheme, www., or query string. Capture group 1 is the referral pointer.'
            : 'Tested against the URL body — no scheme, www., or query string. Recognizes the portal; the patient is identified by the primary ID field, so no capture group is needed.'}
        </RegexHelpText>
      </InputGroup>

      {isEditing && (
        <>
          <SectionHeader>Primary ID</SectionHeader>

          <InputGroup title='Identifies a retrieval on the portal'>
            <SelectWrapper>
              <Select
                inputId='primary-id'
                options={primaryIdOptions}
                value={primaryIdOptions.find((option) => option.value === values.primaryId) ?? null}
                getOptionValue={(option: PrimaryIdOption | null) => option!.value}
                getOptionLabel={(option: PrimaryIdOption | null) => option!.label}
                onChange={(option: PrimaryIdOption | null) => form.setValue('primaryId', option?.value ?? 'url')}
                isDisabled={!canEdit}
              />
            </SelectWrapper>
          </InputGroup>

          {values.primaryId !== 'url' && (
            <InputGroup title='Referral identified by URL' error={getFieldError(form.errors, 'urlIdentifiesReferral')}>
              <Checkbox
                size={20}
                checked={values.urlIdentifiesReferral}
                disabled={!canEdit}
                onChange={(on) => form.setValue('urlIdentifiesReferral', on)}
                dataCyLabel='url-identifies-referral'
                label='This portal gives each referral its own URL'
              />
              <RegexHelpText>
                Tick only when capture group 1 of the pattern above differs per referral. It lets a scan and a manual
                capture of the same referral meet on one record. Leaving it off is always safe; ticking it for a URL
                that is the same on every page makes one patient&apos;s capture land on another&apos;s.
              </RegexHelpText>
            </InputGroup>
          )}

          <SectionHeader>List Page Markers</SectionHeader>

          {/* Portal-wide, not per client: a portal's list pages are the same shape for everyone
              who works it, so whether our markers read correctly there is a fact about the
              portal. Clients never see this surface. */}
          <InputGroup title='Mark referrals on list pages' error={getFieldError(form.errors, 'worklistMarkers')}>
            <Checkbox
              size={20}
              checked={values.worklistMarkers}
              disabled={!canEdit}
              onChange={(on) => form.setValue('worklistMarkers', on)}
              dataCyLabel='worklist-markers'
              label="Show what Olio has next to each referral on this portal's queues and worklists"
            />
            <RegexHelpText>
              {values.primaryId === 'url'
                ? 'Links matching the referral URL pattern above get a marker each: already in Olio, being worked, needs attention, or not captured yet.'
                : 'Text matching the identifier pattern below gets a marker each. Text that Olio does not recognize is left alone — an unrecognized string is a guess, not new work.'}
            </RegexHelpText>
          </InputGroup>

          {values.primaryId !== 'url' && (
            <InputGroup title='Identifier pattern' error={getFieldError(form.errors, 'identifierRegex')}>
              <Input
                value={values.identifierRegex}
                onChange={(e) => form.setValue('identifierRegex', e.target.value)}
                placeholder={String.raw`MRN-\d{4,}`}
                $hasError={Boolean(getFieldError(form.errors, 'identifierRegex'))}
                disabled={!canEdit}
                data-testid='identifier-regex'
              />
              <RegexHelpText>
                What a{system ? ` ${primaryIdLabel(system, values.primaryId).toLowerCase()}` : 'n identifier'} looks
                like in this portal&apos;s text. Keep it tight: a loose pattern marks phone numbers and bed numbers too.
              </RegexHelpText>
            </InputGroup>
          )}
        </>
      )}

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={form.goBack}>
          Cancel
        </Button>
        {canEdit && (
          <Button
            data-testid='portal-systems-save'
            onClick={() => form.submit().catch(() => {})}
            disabled={!form.dirty || !form.valid || form.submitting}
            loading={form.submitting}>
            {isEditing ? 'Update Portal System' : 'Create Portal System'}
          </Button>
        )}
      </ButtonGroup>
    </AdminFormPage>
  );
}

function FieldName({ field }: { field: AdminRetrievalField | AdminScanField }) {
  return (
    <FieldNameCell>
      <BoldText>{field.label}</BoldText>
      {field.required && <RequiredTag>required</RequiredTag>}
    </FieldNameCell>
  );
}

const HelpText = styled.p`
  color: ${colors.black50};
  font-size: 14px;
  margin: 0 0 20px;
`;

const RegexHelpText = styled.p`
  color: ${colors.black50};
  font-size: 12px;
  margin: 6px 0 0;
`;

const TableWrapper = styled.div`
  margin-bottom: 16px;
`;

const FieldNameCell = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const BoldText = styled.span`
  font-weight: 500;
`;

const TypeTag = styled.span`
  background: ${colors.black10};
  border-radius: 4px;
  color: ${colors.black50};
  font-size: 11px;
  padding: 2px 6px;
`;

const RequiredTag = styled(TypeTag)`
  color: ${colors.black75};
`;

const SelectWrapper = styled.div`
  max-width: 320px;
`;

const NameInput = styled(Input)`
  max-width: 320px;
`;

const UrlInput = styled(Input)`
  max-width: 420px;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
